import { useRef, useState } from "react";
import { useSaveBriefDocument } from "../../hooks/useCampaigns";

const fields = [
  ["audience", "Audience", false], ["target_outcome", "Target outcome", false],
  ["opportunity", "Opportunity", false], ["channel_plan", "Channel plan (one per line)", true],
  ["distribution_plan", "Distribution plan (one per line)", true],
  ["evidence", "Evidence references (one per line)", true],
  ["claims", "Planning claims (one per line; optional)", true],
] as const;
const lines = (text: string) => text.split("\n").map(x => x.trim()).filter(Boolean);

/** Structured planning input, never a claim verification or a source upload. */
export function ManualBlogBriefEditor({ brandId, campaignId, briefId, metadata, content: priorContent = "" }: {
  brandId: string; campaignId: string; briefId: string; metadata?: Record<string, unknown> | null; content?: string;
}) {
  const form = useRef<HTMLFormElement>(null);
  const save = useSaveBriefDocument(brandId);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const canonical = (metadata?.canonical_brief ?? {}) as Record<string, unknown>;
  const hasVerifiedClaims = Array.isArray(canonical.claims) && canonical.claims.some(claim => claim?.label === "verified");
  function initial(key: string): string {
    const value = canonical[key];
    if (typeof value === "string") return value;
    if (!Array.isArray(value)) return "";
    return value.map(item => typeof item === "string" ? item : item?.reference ?? item?.text ?? "").join("\n");
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(null); setSaved(false);
    const data = new FormData(form.current!);
    const value = (key: string) => String(data.get(key) ?? "").trim();
    for (const [key, label] of fields) {
      if (key !== "claims" && !value(key)) { setError(`${label} is required.`); return; }
    }
    const manual = {
      audience: value("audience"), target_outcome: value("target_outcome"), opportunity: value("opportunity"),
      channel_plan: lines(value("channel_plan")), distribution_plan: lines(value("distribution_plan")),
      evidence: lines(value("evidence")).map(reference => ({ reference })),
      claims: lines(value("claims")).map(text => ({ text, label: "inferred" })),
      ...(value("landing_url") ? { landing_url: value("landing_url") } : {}),
    };
    const structured = fields.map(([key, label, list]) => `## ${key === "claims" ? "Planning notes" : label}\n${list ? lines(value(key)).map(line => `- ${line}`).join("\n") : value(key)}`).join("\n\n")
      + (value("landing_url") ? `\n\n## Landing page\n${value("landing_url")}` : "");
    // Update only our bounded section; unknown outlines and operator notes survive byte-for-byte.
    const start = "<!-- manual-canonical-brief:start -->";
    const end = "<!-- manual-canonical-brief:end -->";
    const block = `${start}\n# Structured Blog brief\n\n${structured}\n${end}`;
    const from = priorContent.indexOf(start);
    const to = from < 0 ? -1 : priorContent.indexOf(end, from + start.length);
    const content = from >= 0 && to >= 0
      ? priorContent.slice(0, from) + block + priorContent.slice(to + end.length)
      : `${priorContent}${priorContent ? "\n\n" : ""}${block}`;
    try {
      await save.mutateAsync({ campaignId, briefId, type: "brief", content, manual_canonical_brief: manual });
      setSaved(true);
    } catch (err) { setError(err instanceof Error ? err.message : "Could not save structured brief."); }
  }
  return <details className="rounded-xl border border-slate-200 p-4" open={!metadata?.canonical_brief}>
    <summary className="cursor-pointer font-medium">Structured Blog brief</summary>
    <p className="my-3 text-sm text-slate-600">Complete this brief, then choose Draft this on the same page. Your saved brief, including prose edits, guides the draft’s perspective, worked example, workflow and structure. Factual examples, quotes and product claims need supporting content saved in this brand’s Corpus; add it there first. Evidence references and claims entered here are planning assertions, not verified facts. Missing support stops the draft from passing review.</p>
    {hasVerifiedClaims && <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">This brief contains verified claims. Saving this form removes their verified status and source mappings. To preserve unchanged verified claims, use the brief’s prose editor instead.</p>}
    <form ref={form} onSubmit={submit} className="space-y-3">
      {fields.map(([key, label]) => <label key={key} className="block text-sm"><span>{label}</span><textarea name={key} defaultValue={initial(key)} className="mt-1 block min-h-20 w-full rounded-lg border border-slate-300 p-2" /></label>)}
      <label className="block text-sm">Landing URL (optional)<input name="landing_url" type="url" defaultValue={initial("landing_url")} className="mt-1 block w-full rounded-lg border border-slate-300 p-2" /></label>
      <button type="submit" disabled={save.isPending} className="rounded-lg bg-slate-950 px-4 py-2 text-white disabled:opacity-50">{save.isPending ? "Saving…" : "Save structured brief"}</button>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {saved && <p role="status" className="text-sm text-emerald-700">Structured brief saved on this brief. Draft this will check sources and campaign readiness before starting.</p>}
    </form>
  </details>;
}
